import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";

import { MapContainer, GeoJSON, ZoomControl } from "react-leaflet";

import axios from "axios";

import { useLocation, useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import "leaflet-fullscreen/dist/Leaflet.fullscreen.js";
import "leaflet-fullscreen/dist/leaflet.fullscreen.css";
import { useMap } from "react-leaflet";
import * as turf from "@turf/turf";

const INDIA_DISTRICTS_GEOJSON_URL =
  "https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@latest/geojson/india.geojson";
const INDIA_STATE_BOUNDARIES_FALLBACK_URL =
  "https://raw.githubusercontent.com/geohacker/india/master/state/india_telengana.geojson";


const normalizeMapLabel = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const DEFAULT_STATE_FILL_COLOR = "#e1e2e4";
const FIGMA_STATE_FILL_COLORS = {
  [normalizeMapLabel("Chhattisgarh")]: "#EDD34B",
  [normalizeMapLabel("Delhi")]: "#A92FDD",
  [normalizeMapLabel("Goa")]: "#DD7731",
  [normalizeMapLabel("Gujarat")]: "#E9CA8A",
  [normalizeMapLabel("Jammu and Kashmir")]: "#FC8888",
  [normalizeMapLabel("Karnataka")]: "#7598D0",
  [normalizeMapLabel("Madhya Pradesh")]: "#5BD6AA",
  [normalizeMapLabel("Maharashtra")]: "#F471A5",
  [normalizeMapLabel("Rajasthan")]: "#56BDCE",
  [normalizeMapLabel("Tamil Nadu")]: "#C6CE5E",
  [normalizeMapLabel("Uttar Pradesh")]: "#C1A6ED",
};

const getFigmaStateFillColor = (stateName) =>
  FIGMA_STATE_FILL_COLORS[normalizeMapLabel(stateName)] ||
  DEFAULT_STATE_FILL_COLOR;


function SimpleModal({ open, onClose, stateName }) {
  const navigate = useNavigate();
  if (!open) return null;

  const handleExpand = () => {
    const slug = stateName.toLowerCase().replace(/\s+/g, "-");
    navigate(`/sample-page`);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <span
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            fontSize: "20px",
            fontWeight: "bold",
            cursor: "pointer",
            color: "#333",
          }}
        >
          ×
        </span>

        <h2 style={styles.title}>{stateName}</h2>

        <p style={{ marginTop: "10px" }}>
          Chapter is present in <strong>{stateName}</strong>.
        </p>

        <div
          style={{
            marginTop: "20px",
            display: "flex",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <button style={styles.expandButton} onClick={handleExpand}>
            Expand
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.3)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  modal: {
    background: "white",
    padding: "20px 30px",
    borderRadius: "10px",
    minWidth: "300px",
    position: "relative", 
  },
  expandButton: {
    background: "#007bff",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "bold",
  },
  closeButton: {
    marginTop: "18px",
    padding: "8px 14px",
    background: "#3498db",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

function CountryMap({
  enableCustomizer = true,
  title = "India",
  featureEffects = true,
  description = "Click/Hover States",
  onStateClick,
}) {
  const [data, setData] = useState({});
  const [chapterData, setChapterData] = useState([]);
  const [states, setStates] = useState([]);
  const [customizerActive, setCustomizerActive] = useState(false);
  const [fillOpacity, setFillOpacity] = useState(100);
  const [fillColor, setFillColor] = useState("#3498db");
  const [borderColor, setBorderColor] = useState("#000000");
  const [borderWidth, setBorderWidth] = useState(1);
  const MP_SPLIT_LONGITUDE = 78; 
   const [modalOpen, setModalOpen] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [fallbackStateBoundaries, setFallbackStateBoundaries] = useState(null);

  const theme = useTheme();

  const getFeatureStateName = useCallback((feature) => {
    return (
      feature?.properties?.st_nm ||
      feature?.properties?.NAME_1 ||
      feature?.properties?.state ||
      ""
    );
  }, []);

  const dissolvedStateData = useMemo(() => {
    const features = data?.features;
    if (!Array.isArray(features) || !features.length) return null;

    try {
      const districtFc = turf.featureCollection(features);
      const flattened = turf.flatten(districtFc);
      const stateFc = turf.dissolve(flattened, { propertyName: "st_nm" });
      return stateFc?.features?.length ? stateFc : null;
    } catch (error) {
      console.error("State dissolve failed:", error);
      return null;
    }
  }, [data]);

  const districtFillStyle = useCallback((feature) => {
    return {
      fillOpacity: 1,
      weight: 0.4,
      color: "#ffffff",
      fillColor: getFigmaStateFillColor(getFeatureStateName(feature)),
    };
  }, [getFeatureStateName]);

  const stateBoundaryStyle = useCallback(() => {
    return {
      fillOpacity: 0,
      weight: 1.2,
      color: "#ffffff",
      fillColor: "#ffffff",
    };
  }, []);



  const currentRoute = useLocation().pathname.replace("/", "");

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(INDIA_DISTRICTS_GEOJSON_URL);
      const result = await response.json();
      setData(result || {});
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchFallbackStateBoundaries = async () => {
      try {
        const response = await fetch(INDIA_STATE_BOUNDARIES_FALLBACK_URL);
        const result = await response.json();
        setFallbackStateBoundaries(result || null);
      } catch (error) {
        console.error("Failed to fetch fallback state boundaries:", error);
        setFallbackStateBoundaries(null);
      }
    };

    fetchFallbackStateBoundaries();
  }, []);
    useEffect(() => {
    const fetchChapters = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/getAllChapters`
        );
        const payload = res?.data?.data ?? res?.data ?? [];
        const normalized = Array.isArray(payload)
          ? payload
          : payload
          ? [payload]
          : [];
        setChapterData(normalized);;
      } catch (err) {
        console.error("Error fetching chapters:", err);
        setChapterData([]);
      }
    };

    fetchChapters();
  }, []);
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/states`);
        const payload = res?.data?.data ?? res?.data ?? [];
        setStates(Array.isArray(payload) ? payload : []);
      } catch (err) {
        console.error("Error fetching states:", err);
        setStates([]);
      }
    };

    fetchStates();
  }, []);

  


  const normalizeLabel = normalizeMapLabel;

  const extractId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value.$oid) return value.$oid;
    if (value._id) return extractId(value._id);
    return "";
  };

  const getChapterStateId = (chapter) => {
    const state = chapter?.location?.state;
    return extractId(state);
  };

  const stateIdByName = useMemo(() => {
    const map = new Map();
    (states || []).forEach((s) => {
      const key = normalizeLabel(s?.stateName);
      const id = extractId(s?._id);
      if (key && id) map.set(key, id);
    });
    return map;
  }, [states]);

  const mpStateIds = useMemo(() => {
    const ids = new Set();
    (states || []).forEach((s) => {
      const name = normalizeLabel(s?.stateName);
      if (name.startsWith("madhya pradesh")) {
        const id = extractId(s?._id);
        if (id) ids.add(id);
      }
    });
    return ids;
  }, [states]);

  const getChapterCount = useCallback((name) => {
    const label = normalizeLabel(name);

    if (label === "madhya pradesh") {
      if (!mpStateIds.size) return 0;
      return chapterData.filter((ch) =>
        mpStateIds.has(String(getChapterStateId(ch)))
      ).length;
    }

    const stateId = stateIdByName.get(label);
    if (!stateId) return 0;

    return chapterData.filter(
      (ch) => String(getChapterStateId(ch)) === String(stateId)
    ).length;
  }, [chapterData, stateIdByName, mpStateIds]);
  const onEachStates = useCallback((state, layer) => {
    let baseStateName = getFeatureStateName(state);

    if (baseStateName !== "Madhya Pradesh") {
      const chapterCount = getChapterCount(baseStateName);
      if (chapterCount > 0) {
        layer.bindTooltip(
          `${baseStateName} - ${chapterCount} chapter${chapterCount !== 1 ? "s" : ""}`,
          {
            sticky: true,
            direction: "top",
            offset: [0, -10],
          }
        );
      }
    }

    layer.on({
      mousemove: (event) => {
        let label = baseStateName;

        if (baseStateName === "Madhya Pradesh") {
          label = "Madhya Pradesh";
        }

        const chapterCount = getChapterCount(label);

        if (chapterCount > 0) {
          layer
            .bindTooltip(
              `${label} - ${chapterCount} chapter${chapterCount !== 1 ? "s" : ""}`,
              {
                sticky: true,
                direction: "top",
                offset: [0, -10],
              }
            )
            .openTooltip();
        } else {
          layer.closeTooltip();
        }

        layer.setStyle({ dashArray: "0 4 0" });
      },

      mouseout: () => {
        layer.setStyle({ dashArray: "" });
        layer.closeTooltip();
      },

      click: (event) => {
        const feature = event.target.feature;
        const id = feature?.properties?.id || feature?.properties?.ID_1;
        const name = getFeatureStateName(feature);

        if (onStateClick) onStateClick(name);
        setSelectedState({ id, name });
      },
    });
  }, [getChapterCount, onStateClick, getFeatureStateName]);

  function ResizeHandler() {
    const map = useMap();

    useEffect(() => {
      setTimeout(() => {
        map.invalidateSize();
      }, 200);
    }, []);

    return null;
  }

  if (Object.keys(data).length === 0)
    return (
      <>
        {/* <Header title={title} /> */}
        {/* <Loader /> */}
      </>
    );

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {" "}
      <SimpleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        stateName={selectedState}
      />
    
      {!selectedState && (
        (() => {
          const interactiveStateFeatures =
            dissolvedStateData?.features || fallbackStateBoundaries?.features || [];

          return (
        <MapContainer
          center={[22, 80]}
          // zoom={4.65}
          zoom={4.3}
          zoomSnap={0}
          zoomDelta={0.1}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
          zoomControl={false}
          attributionControl={false}
          style={{
            height: "100%",
            width: "100%",
            flex: 1,
            backgroundColor: "transparent",
            // backgroundColor: theme.palette.background.slate,
            // borderLeft: "1px solid grey",
          }}
        >
          <GeoJSON
            key={`district-fill-${chapterData.length}`}
            data={data.features}
            style={districtFillStyle}
          />
          {interactiveStateFeatures.length > 0 && (
            <GeoJSON
              key={`state-boundary-${chapterData.length}`}
              data={interactiveStateFeatures}
              style={stateBoundaryStyle}
              onEachFeature={onEachStates}
            />
          )}
          <ZoomControl position="bottomright" />
        </MapContainer>
          );
        })()
      )}
    </div>
  );
}

export default CountryMap;









































